import React, { useState } from 'react';
import { Box, Button } from '@mui/material';

import { useIsMounted } from '@paragon/client-tools';

/**
 * A hook to easily add edit/save/cancel buttons to a properties panel, for when the default apply-on-blur behavior
 *   isn't what you want.  See EndConditionSettings.tsx for example usage.
 * @param onSave A function that will accept the edited value and apply it.  Called when the save button is clicked.
 *   Returns a boolean indicating whether or not the save was successful.
 */
export function usePropertiesPanelSaveButtons<T>(onSave: (value: T) => boolean | Promise<boolean>) {
  const [value, setValue] = useState<T | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isMounted = useIsMounted();

  return {
    value,
    setValue,
    isEditing,
    isSaving,
    render: () => (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
        {/* While this is a useful notification, further discussions need to be held to determine the best location for notification like this one that won't conflict with other notifications. For the time being, the cancel dialog will suffice.
        {value && (
          <Snackbar sx={{ ml: 6 }} open>
            <Alert sx={{ color: '#ffffff' }} elevation={6} variant="filled" severity="info">
              You have unsaved changes which are not previewed on the truss. Click save to apply all changes.{' '}
            </Alert>
          </Snackbar>
        )} */}
        <Box>
          <Button
            sx={{ m: 1, width: 200 }}
            disabled={isSaving}
            variant="outlined"
            onClick={() => {
              setValue(null);
              setIsEditing(false);
            }}
          >
            Cancel
          </Button>

          <Button
            sx={{ m: 1, width: 200 }}
            disabled={isSaving || value == null}
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!value) {
                return;
              }

              setIsSaving(true);
              try {
                const success = await onSave(value);
                if (success && isMounted()) {
                  setValue(null);
                  setIsEditing(false);
                }
              } finally {
                if (isMounted()) {
                  setIsSaving(false);
                }
              }
            }}
          >
            Save
          </Button>
        </Box>
      </Box>
    ),
  };
}
